/**
 * The Integrations tab's calls — Square and Dext. The contract is
 * docs/integrations.md; the shapes are `@baros/core`'s, so the screen and the
 * API cannot drift apart on what a till item or a connection is.
 *
 * The transport is screen 12's own `request`, so a refusal arrives as an
 * `OrgApiError` whose message is what the API said. That matters here more
 * than usual: a 400 on connect carries Square's own words about the token,
 * and the screen shows them as they came.
 *
 * Nothing in this file ever receives the Square token back. It goes up once,
 * in `connectSquare`, and no route returns it.
 */

import type {
  DextIntegration,
  PosEnvironment,
  PosIntegration,
  PosItem,
  PosSyncState,
  PosTarget,
} from "@baros/core";
import { request } from "./api.js";

const id = encodeURIComponent;

const SQUARE = "/v1/org/integrations/square";
const DEXT = "/v1/org/integrations/dext";
const ITEMS = "/v1/org/pos/items";

export interface SquareState {
  /** Null until somebody connects. */
  readonly square: PosIntegration | null;
  /** Venue id → how that venue's last sync went. Absent until it has run once. */
  readonly sync: Readonly<Record<string, PosSyncState>>;
}

export interface DextState {
  readonly dext: DextIntegration | null;
  /** The address slips are sent from. */
  readonly sender: string;
  /** False while SES will not send from it — see integrations.md. */
  readonly senderReady: boolean;
}

export const integrationsApi = {
  square: (token: string) => request<SquareState>(token, "GET", SQUARE),

  /** Connect, or replace the token on an existing connection. */
  connectSquare: (token: string, accessToken: string, environment: PosEnvironment) =>
    request<{ square: PosIntegration }>(token, "PUT", SQUARE, { accessToken, environment }),

  disconnectSquare: (token: string) => request<{ removed: boolean }>(token, "DELETE", SQUARE),

  /** `locationId: null` unlinks the venue. */
  linkVenue: (token: string, venueId: string, locationId: string | null) =>
    request<{ square: PosIntegration }>(token, "PUT", `${SQUARE}/venues/${id(venueId)}`, {
      locationId,
    }),

  setOptions: (token: string, decrementStock: boolean) =>
    request<{ square: PosIntegration }>(token, "PUT", `${SQUARE}/options`, { decrementStock }),

  items: (token: string) => request<{ items: readonly PosItem[] }>(token, "GET", ITEMS),

  /** Read the Square catalogue, add anything not seen before, re-guess the unconfirmed. */
  refreshItems: (token: string) =>
    request<{ items: readonly PosItem[]; added: number; rematched: number; nights: number }>(
      token,
      "POST",
      `${ITEMS}/refresh`,
    ),

  /**
   * Say what one till item is. The key is encoded because an open-price
   * line's is `name:bar-snacks`, colon and all.
   */
  saveItem: (token: string, key: string, target: PosTarget | null) =>
    request<{ item: PosItem }>(token, "PUT", `${ITEMS}/${id(key)}`, { target }),

  dext: (token: string) => request<DextState>(token, "GET", DEXT),

  saveDext: (token: string, emailAddress: string, autoSend: boolean) =>
    request<{ dext: DextIntegration; recognised: boolean }>(token, "PUT", DEXT, {
      emailAddress,
      autoSend,
    }),

  removeDext: (token: string) => request<{ removed: boolean }>(token, "DELETE", DEXT),
};
